import type { Text } from 'troika-three-text'
import { isCadWebGpuTextMaterial } from '@/plugins/cad/module/CADModule/object/text/CadTextFactory'
import { getLoggerManager } from '@/shared/logging'
import { LogLevel } from '@/shared/logging/LogLevel'

const pending = new Set<Text>()
const submitted = new WeakMap<Text, string>()
const disposed = new WeakSet<Text>()
const log = getLoggerManager().getLogger('cad:diagnostics')
let scheduled = false
let submissions = 0
let syncCpuMs = 0
let completionMs = 0

function layoutKey(text: Text): string {
  return JSON.stringify([
    text.text,
    text.font,
    text.fontSize,
    text.fontWeight,
    text.fontStyle,
    text.maxWidth,
    text.whiteSpace,
    text.overflowWrap,
    text.anchorX,
    text.anchorY,
    text.textAlign,
    text.lineHeight,
    text.letterSpacing,
    text.direction,
    text.lang,
    text.sdfGlyphSize,
  ])
}

/** 合并同一轮文本修改；颜色与空间变换不需要重新排版。 */
export function scheduleCadTextSync(text: Text): void {
  if (disposed.has(text) || submitted.get(text) === layoutKey(text)) return
  pending.add(text)
  if (scheduled) return
  scheduled = true
  queueMicrotask(flushCadTextSync)
}

/** 渲染前或微任务结束时提交排版，诊断关闭时不读取时钟。 */
export function flushCadTextSync(): void {
  scheduled = false
  if (!pending.size) return
  const manager = getLoggerManager()
  const tracking = manager.isPerfEnabled() || log.isEnabled(LogLevel.DEBUG)
  const texts = [...pending]
  pending.clear()
  for (const text of texts) {
    if (disposed.has(text)) continue
    const key = layoutKey(text)
    if (submitted.get(text) === key) continue
    submitted.set(text, key)
    const started = tracking ? manager.now() : 0
    try {
      text.sync(
        tracking
          ? () => {
              completionMs += manager.now() - started
            }
          : undefined
      )
    } catch (error) {
      submitted.delete(text)
      throw error
    } finally {
      if (tracking) {
        submissions += 1
        syncCpuMs += manager.now() - started
      }
    }
  }
}

/** 删除对象时取消尚未提交的同步，保留 Troika 的资源释放语义。 */
export function disposeCadText(text: Text): void {
  pending.delete(text)
  submitted.delete(text)
  disposed.add(text)
  const derived = (text as unknown as { _derivedMaterial?: unknown })._derivedMaterial
  const material = Array.isArray(derived) ? derived[0] : derived
  if (isCadWebGpuTextMaterial(material)) material.dispose()
  text.dispose()
}

/** 诊断开启期间的累计同步统计；异步耗时包括排版队列等待时间。 */
export function getCadTextSyncStats() {
  return { submissions, pending: pending.size, syncCpuMs, completionMs }
}
